import { useEffect, useState } from 'react';
import { Review } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { formatDate, getStatusColor, getModerationStatusColor } from '@/lib/utils';
import { Check, X, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';

interface ReviewDetailDialogProps {
  review: Review | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (reviewId: string, notes?: string) => void;
  onReject: (reviewId: string, notes?: string) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

export function ReviewDetailDialog({
  review,
  open,
  onOpenChange,
  onApprove,
  onReject,
  onNavigate,
  hasPrev = false,
  hasNext = false,
}: ReviewDetailDialogProps) {
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setNotes('');
  }, [review?.reviewId]);

  useEffect(() => {
    if (!open || !onNavigate) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate('prev');
      if (e.key === 'ArrowRight' && hasNext) onNavigate('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onNavigate, hasPrev, hasNext]);

  if (!review) return null;

  const handleApprove = () => {
    onApprove(review.reviewId, notes || undefined);
    setNotes('');
    onOpenChange(false);
  };

  const handleReject = () => {
    onReject(review.reviewId, notes || undefined);
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Review Details</DialogTitle>
              <DialogDescription>Review ID: {review.reviewId}</DialogDescription>
            </div>
            {onNavigate && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!hasPrev}
                  onClick={() => onNavigate('prev')}
                  aria-label="Previous review"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!hasNext}
                  onClick={() => onNavigate('next')}
                  aria-label="Next review"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status Badges */}
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(review.status)}>{review.status}</Badge>
            <Badge className={getModerationStatusColor(review.moderationStatus)}>
              {review.moderationStatus}
            </Badge>
          </div>

          {/* Product and User Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold mb-1">Product</h4>
              <p className="text-sm">{review.productName}</p>
              <p className="text-xs text-muted-foreground">SKU: {review.productSku}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">User</h4>
              <p className="text-sm">{review.userName}</p>
              <p className="text-xs text-muted-foreground">Customer Number: {review.customerNumber}</p>
            </div>
          </div>

          {/* Rating */}
          <div>
            <h4 className="text-sm font-semibold mb-1">Rating</h4>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className={i < review.rating ? 'text-yellow-400 text-xl' : 'text-gray-300 text-xl'}
                >
                  ★
                </span>
              ))}
            </div>
          </div>

          {/* Review Content */}
          <div>
            <h4 className="text-sm font-semibold mb-1">Title</h4>
            <p className="text-base font-medium">{review.title}</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-1">Content</h4>
            <p className="text-sm whitespace-pre-wrap">{review.content}</p>
          </div>

          {/* Flags */}
          {review.flags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Flags ({review.flags.length})
              </h4>
              <div className="space-y-2">
                {review.flags.map((flag, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg bg-orange-50"
                  >
                    <div>
                      <Badge variant="outline" className="mb-1">
                        {flag.type}
                      </Badge>
                      {flag.details && (
                        <p className="text-xs text-muted-foreground mt-1">{flag.details}</p>
                      )}
                    </div>
                    <div className="text-sm font-medium">
                      {(flag.confidence * 100).toFixed(0)}% confidence
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-1">Created</h4>
              <p className="text-muted-foreground">{formatDate(review.createdAt)}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Updated</h4>
              <p className="text-muted-foreground">{formatDate(review.updatedAt)}</p>
            </div>
            {review.moderatedAt && (
              <>
                <div>
                  <h4 className="font-semibold mb-1">Moderated</h4>
                  <p className="text-muted-foreground">{formatDate(review.moderatedAt)}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Moderated By</h4>
                  <p className="text-muted-foreground">{review.moderatedBy}</p>
                </div>
              </>
            )}
          </div>

          {/* Previous Notes */}
          {review.moderatorNotes && (
            <div>
              <h4 className="text-sm font-semibold mb-1">Previous Moderator Notes</h4>
              <div className="p-3 border rounded-lg bg-muted/50">
                <p className="text-sm">{review.moderatorNotes}</p>
              </div>
            </div>
          )}

          {/* Moderator Notes Input */}
          {review.status === 'Pending' && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Moderator Notes (Optional)</h4>
              <Textarea
                placeholder="Add notes about your moderation decision..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        {review.status === 'Pending' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleReject} variant="destructive" className="gap-2">
              <X className="h-4 w-4" />
              Reject
            </Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700 gap-2">
              <Check className="h-4 w-4" />
              Approve
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
